/**
 * Sound.
 *
 * Every sound is synthesised at runtime with WebAudio — no audio files, so the game
 * stays one self-contained bundle that loads instantly. Browsers refuse to start audio
 * until the player interacts, so the context is created lazily on the first gesture.
 *
 * This exists because the game was silent, and silence made working mechanics feel
 * broken: biting an animal dealt damage with no bite sound, and buying a morph tool
 * spent coupons with no chime. The feedback matters as much as the mechanic.
 */

import { Ambience } from './ambience'

export type Sfx =
  | 'bite' | 'hit' | 'kill' | 'hurt'
  | 'jump' | 'land' | 'step'
  | 'coupon' | 'purchase' | 'card' | 'morph'
  | 'dig' | 'thunder' | 'win' | 'lose'

export class Audio {
  private ctx: AudioContext | null = null
  private master: GainNode | null = null
  private rainSource: AudioBufferSourceNode | null = null
  private rainGain: GainNode | null = null
  /** Jungle bed and the generative music. Built once the context exists. */
  ambience: Ambience | null = null
  muted = false

  /** Created on the first real gesture — browsers block audio before one. */
  private ensure(): AudioContext | null {
    if (this.ctx) return this.ctx
    try {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      this.ctx = new Ctor()
      this.master = this.ctx.createGain()
      this.master.gain.value = 0.16
      this.master.connect(this.ctx.destination)
      this.ambience = new Ambience(this.ctx, this.master)
    } catch {
      return null
    }
    return this.ctx
  }

  /** Call from a click/touch handler so the context is allowed to start. */
  unlock(): void {
    const ctx = this.ensure()
    if (ctx && ctx.state === 'suspended') void ctx.resume()
  }

  /** Advance the jungle and the music. Safe before the context exists. */
  updateAmbience(dt: number, daylight: number, wet: boolean): void {
    this.ambience?.update(dt, daylight, wet)
  }

  setMuted(muted: boolean): void {
    this.muted = muted
    if (this.master) this.master.gain.value = muted ? 0 : 0.35
  }

  /** A short shaped tone. The building block for most cues. */
  private tone(freq: number, duration: number, type: OscillatorType,
               gain = 0.5, sweepTo?: number): void {
    const ctx = this.ensure()
    if (!ctx || !this.master || this.muted) return
    const osc = ctx.createOscillator()
    const env = ctx.createGain()
    // Roll the top off every tone. Raw square/saw edges were the harshness.
    const soften = ctx.createBiquadFilter()
    soften.type = 'lowpass'
    soften.frequency.value = 2200
    soften.Q.value = 0.4
    osc.type = type
    osc.frequency.setValueAtTime(freq, ctx.currentTime)
    if (sweepTo !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, sweepTo), ctx.currentTime + duration)
    }
    env.gain.setValueAtTime(0, ctx.currentTime)
    // A slower attack removes the click that made short cues sound like static.
    env.gain.linearRampToValueAtTime(gain, ctx.currentTime + 0.022)
    env.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration)
    osc.connect(soften).connect(env).connect(this.master)
    osc.start()
    osc.stop(ctx.currentTime + duration + 0.02)
  }

  /** A burst of filtered noise — teeth, footsteps, digging, thunder. */
  private noise(duration: number, cutoff: number, gain = 0.4, type: BiquadFilterType = 'lowpass'): void {
    const ctx = this.ensure()
    if (!ctx || !this.master || this.muted) return
    const frames = Math.max(1, Math.floor(ctx.sampleRate * duration))
    const buffer = ctx.createBuffer(1, frames, ctx.sampleRate)
    const data = buffer.getChannelData(0)
    for (let i = 0; i < frames; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / frames)
    const source = ctx.createBufferSource()
    source.buffer = buffer
    const filter = ctx.createBiquadFilter()
    filter.type = type
    filter.frequency.value = cutoff
    filter.Q.value = 0.6
    const env = ctx.createGain()
    env.gain.setValueAtTime(0, ctx.currentTime)
    env.gain.linearRampToValueAtTime(gain, ctx.currentTime + 0.015)
    env.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration)
    source.connect(filter).connect(env).connect(this.master)
    source.start()
  }

  play(sfx: Sfx): void {
    switch (sfx) {
      // A snap of teeth: a noise crunch over a low thump.
      case 'bite':
        this.noise(0.07, 900, 0.12, 'lowpass')
        this.tone(150, 0.1, 'sine', 0.1, 90)
        return
      case 'hit':
        this.noise(0.1, 600, 0.16)
        this.tone(110, 0.13, 'sine', 0.12, 65)
        return
      case 'kill':
        this.tone(380, 0.4, 'triangle', 0.14, 120)
        this.noise(0.22, 420, 0.1)
        return
      case 'hurt':
        this.tone(200, 0.2, 'sine', 0.13, 120)
        return
      case 'jump':
        this.tone(340, 0.14, 'sine', 0.1, 560)
        return
      case 'land':
        this.noise(0.08, 260, 0.1)
        return
      case 'step':
        this.noise(0.04, 420, 0.035)
        return
      // Two bright notes up — a ticket being taken.
      case 'coupon':
        this.tone(784, 0.1, 'sine', 0.12)
        window.setTimeout(() => this.tone(1046, 0.13, 'sine', 0.1), 80)
        return
      case 'purchase':
        this.tone(523, 0.11, 'sine', 0.11)
        window.setTimeout(() => this.tone(659, 0.11, 'sine', 0.11), 95)
        window.setTimeout(() => this.tone(784, 0.2, 'sine', 0.1), 190)
        return
      // A rarer, sparklier arpeggio for a squishy card.
      case 'card':
        [0, 90, 180, 280].forEach((delay, i) => {
          window.setTimeout(() => this.tone([523, 659, 784, 1046][i]!, 0.28, 'sine', 0.1), delay)
        })
        return
      case 'morph':
        this.tone(180, 0.38, 'sine', 0.12, 700)
        this.noise(0.32, 700, 0.06, 'lowpass')
        return
      case 'dig':
        this.noise(0.14, 300, 0.09)
        return
      case 'thunder':
        this.noise(1.2, 200, 0.18)
        this.tone(58, 1.1, 'sine', 0.14, 34)
        return
      case 'win':
        [0, 110, 220].forEach((delay, i) => {
          window.setTimeout(() => this.tone([523, 659, 784][i]!, 0.32, 'sine', 0.11), delay)
        })
        return
      case 'lose':
        this.tone(392, 0.4, 'sine', 0.1, 261)
        return
    }
  }

  /** Rain is a continuous bed rather than a one-shot, so it loops while wet. */
  setRain(active: boolean, heavy = false): void {
    const ctx = this.ensure()
    if (!ctx || !this.master) return

    if (!active) {
      if (this.rainGain) this.rainGain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.6)
      return
    }

    if (!this.rainSource) {
      const frames = ctx.sampleRate * 2
      const buffer = ctx.createBuffer(1, frames, ctx.sampleRate)
      const data = buffer.getChannelData(0)
      for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1
      const source = ctx.createBufferSource()
      source.buffer = buffer
      source.loop = true
      const filter = ctx.createBiquadFilter()
      filter.type = 'bandpass'
      filter.frequency.value = 750
      filter.Q.value = 0.4
      const gain = ctx.createGain()
      gain.gain.value = 0
      source.connect(filter).connect(gain).connect(this.master)
      source.start()
      this.rainSource = source
      this.rainGain = gain
    }
    this.rainGain!.gain.linearRampToValueAtTime(heavy ? 0.16 : 0.08, ctx.currentTime + 0.8)
  }
}
